import React from 'react';
import { Button, Intent } from '@blueprintjs/core';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import Toast from 'helpers/toaster';
import { FieldError } from 'components/FormBits';
import { SettingsCard } from 'components/SettingsPage';
import CardList from 'components/Billing/CardList';

// Billing > Card: the cards on file, and a form to add one. The card number
// is entered in Stripe's own field (mounted into #card-element); we only ever
// see the token Stripe returns.
@inject('billingStore') @observer
class BillingCreditCard extends React.Component {

  @observable cardHolderName = '';
  @observable cardComplete = false;
  @observable cardError = null;
  @observable loading = false;

  componentDidMount() {
    if (window.stripe) {
      const elements = window.stripe.elements();
      this.card = elements.create('card', { hidePostalCode: true });
      this.card.mount('#card-element');
      this.card.addEventListener('change', this.onCardChange);
    }
  }

  componentWillUnmount() {
    if (this.card && this.card.destroy) this.card.destroy();
  }

  get canSubmit() {
    return this.cardComplete && !this.cardError && this.cardHolderName.trim() !== '' && !this.loading;
  }

  onCardChange = (event) => {
    this.cardError = event.error ? event.error.message : null;
    this.cardComplete = Boolean(event.complete);
  }

  onCardHolderNameChange = (event) => {
    this.cardHolderName = event.target.value;
  }

  onFormSubmit = (event) => {
    event.preventDefault();
    if (!this.canSubmit) return;
    this.loading = true;
    window.stripe.createToken(this.card, {
      name: this.cardHolderName.trim(),
      currency: 'CAD'
    }).then((result) => {
      if (result.error) {
        this.loading = false;
        this.cardError = result.error.message;
        return;
      }
      this.props.billingStore.addCard(result.token)
        .then(this.onCardSuccess)
        .catch(this.onFailAddCard);
    }).catch(this.onFailAddCard);
  }

  onCardSuccess = () => {
    this.cardHolderName = '';
    this.cardComplete = false;
    if (this.card) this.card.clear();
    this.loading = false;
    Toast.show({ message: 'Card added.', intent: Intent.SUCCESS });
  }

  onFailAddCard = () => {
    this.loading = false;
    Toast.show({ message: "Couldn't add the card. Check the details and try again.", intent: Intent.DANGER });
  }

  render() {
    return (
      <React.Fragment>
        <CardList />
        <SettingsCard
          title="Add a card"
          description="Card details go straight to Stripe, our payment processor."
          footer={(
            <Button
              type="submit"
              form="billing-add-card"
              className="sp-button sp-button--primary"
              icon="credit-card"
              text="Add card"
              disabled={!this.canSubmit}
              loading={this.loading}
            />
          )}
        >
          <form id="billing-add-card" className="bill-card-form" onSubmit={this.onFormSubmit} noValidate>
            <div className="bp3-form-group">
              <label className="fb-label" htmlFor="billing-card-name">Name on card</label>
              <input
                id="billing-card-name"
                type="text"
                className="bp3-input bp3-fill"
                placeholder="As it appears on the card"
                autoComplete="cc-name"
                value={this.cardHolderName}
                onChange={this.onCardHolderNameChange}
              />
            </div>
            <div className={`bp3-form-group${this.cardError ? ' has-error' : ''}`}>
              <span className="fb-label" id="billing-card-details-label">Card details</span>
              <div id="card-element" className="bill-card-element" aria-labelledby="billing-card-details-label" />
              <FieldError id="card-errors">{this.cardError}</FieldError>
            </div>
          </form>
        </SettingsCard>
      </React.Fragment>
    );
  }
}

export default BillingCreditCard;
